// Renders one paragraph's markdown to sanitised HTML for the inactive state.
// Each block renders on its own (design §18.7), which is what makes live
// preview free: no decoration layer, just a renderer per block.
//
// Mathematics is written the way it is written everywhere else — $ for a
// formula in a sentence, $$ for one on its own line — and drawn by KaTeX.
// KaTeX rather than MathJax because this runs synchronously, once per block,
// on every render: `processSync` is the whole shape of this file, and KaTeX
// is a function call where MathJax is a document.
//
// Note the order: sanitising happens BEFORE KaTeX, not after. remark-math
// hands on the formula as ordinary text inside a marked <code>, and KaTeX
// replaces it afterwards. So the only thing the sanitiser has to permit is
// that mark. Were it to run last it would instead have to permit the whole of
// KaTeX's output — every positioned <span style>, which is to say `style` on
// spans in general, which is to say CSS injection. Nothing the author writes
// reaches the page unescaped this way: the TeX is a string that KaTeX itself
// escapes.

import rehypeKatex, { type Options as KatexOptions } from "rehype-katex";
import rehypeSanitize, { type Options as Schema, defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import remarkMath from "remark-math";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

/** The classes remark-math marks a formula with, and the only thing the
 *  sanitiser is asked to let through on its account. `language-math` already
 *  passes the default schema's `^language-.` rule for <code>; the other two
 *  do not, and they are what rehype-katex actually looks for. */
const MATH_CLASSES = ["language-math", "math-inline", "math-display"];

// Typed rather than inferred: a spread widens the attribute tuples enough
// that unified picks its `use(plugin, boolean)` overload instead.
const schema: Schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ["className", ...MATH_CLASSES]],
  },
};

const katex: KatexOptions = {
  // KaTeX warns by default, and this renders every inactive block on every
  // render — a warning here would be a warning a thousand times over.
  strict: "ignore",
  // `throwOnError` is not settable: rehype-katex forces it off, which is what
  // this wants anyway. A formula is half-typed for as long as it takes to
  // type it, and throwing would blank the block mid-keystroke. It marks the
  // error in place instead, which `.katex-error` below is drawn for.
};

const processor = unified()
  .use(remarkParse)
  .use(remarkMath)
  .use(remarkRehype)
  .use(rehypeSanitize, schema)
  .use(rehypeKatex, katex)
  .use(rehypeStringify);

// Values are much larger than they were — a display formula is kilobytes of
// markup where a paragraph was bytes — so the ceiling came down with them.
const CACHE_MAX = 400;

const cache = new Map<string, string>();

export function renderBlock(text: string): string {
  const hit = cache.get(text);
  if (hit !== undefined) return hit;
  const html = String(processor.processSync(text));
  if (cache.size > CACHE_MAX) cache.clear();
  cache.set(text, html);
  return html;
}
